import { createContext, useContext, ReactNode } from 'react'

/**
 * Factory genérico para crear un Provider/Context de filtros
 * Reutilizable para cualquier módulo (containers, inventory, billing, etc.)
 *
 * @example
 * ```tsx
 * // En tu archivo de filtros:
 * export const {
 *   FiltersProvider: ContainerFiltersProvider,
 *   useFiltersContext: useContainerFiltersContext
 * } = createFiltersContext(useContainerFilters)
 * ```
 */
export function createFiltersContext<T>(useFiltersHook: () => T) {
  // Crear el contexto
  const FiltersContext = createContext<T | undefined>(undefined)

  // Provider component
  function FiltersProvider({ children }: { children: ReactNode }) {
    const filtersState = useFiltersHook()

    return (
      <FiltersContext.Provider value={filtersState}>
        {children}
      </FiltersContext.Provider>
    )
  }

  // Hook para consumir el contexto
  function useFiltersContext() {
    const context = useContext(FiltersContext)
    if (context === undefined) {
      throw new Error('useFiltersContext must be used within a FiltersProvider')
    }
    return context
  }

  return {
    FiltersProvider,
    useFiltersContext,
  }
}
